import { AlertCircle } from "lucide-react";
import { useMemo } from "react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatTaxLabel } from "@/shared/utils/paymentTaxes";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useTaxMutations } from "../../hooks/useTaxMutations";
import type { ProviderRailRoute } from "../../types/paymentFees.types";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { corridorLabel } from "../../utils/feeFormat";
import { ownTaxRemovalPlan } from "../../utils/taxCoverage";
import { CorridorIdentity } from "../fees/CorridorIdentity";

export interface RemoveOwnTaxDialogProps {
  route: ProviderRailRoute;
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onRemoved: () => void;
  rows: PaymentTax[];
  routes: ProviderRailRoute[];
  now: number;
}

/** "Back to the general rate" on a corridor: every own rule the organization pays there, every version. */
export function RemoveOwnTaxDialog({
  route,
  organizationId,
  organizationName,
  onClose,
  onRemoved,
  rows,
  routes,
  now,
}: RemoveOwnTaxDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const labels = useFeeLabels();
  const { isSubmitting, error, removeTaxes } = useTaxMutations();
  const plan = useMemo(
    () => ownTaxRemovalPlan(route, organizationId, rows, routes, now),
    [route, organizationId, rows, routes, now],
  );

  const rateText = (tax: PaymentTax) =>
    compareDecimalStrings(tax.rate, "0") === 0
      ? `${tax.name} · ${t("exempt")}`
      : formatTaxLabel(tax, activeLanguage);

  const handleConfirm = async () => {
    if (!plan) return;
    if (await removeTaxes(plan.rows.map((tax) => tax.id))) onRemoved();
  };

  return (
    <ColocaAlertDialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
      title={t("remove_title")}
      description={t("remove_description", {
        name: organizationName,
        corridor: corridorLabel(route, labels.directionShort),
      })}
      confirmLabel={t("back_general_confirm")}
      confirmVariant="destructive"
      confirmDisabled={isSubmitting || !plan}
      cancelLabel={t("button_cancel")}
      onConfirm={() => void handleConfirm()}
      onCancel={onClose}
    >
      {plan && (
        <div className="space-y-4">
          {plan.impact.length > 1 && (
            <p className="text-sm">{t("remove_broad_note", { count: plan.impact.length })}</p>
          )}

          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("remove_after_title")}
            </h3>
            <ul className="space-y-2">
              {plan.impact.map((impact) => (
                <li key={impact.route.id} className="text-sm">
                  <CorridorIdentity route={impact.route} className="mb-0.5" />
                  <span className="block font-mono text-xs">
                    {impact.after.length === 0
                      ? t("remove_after_none")
                      : impact.after.map(rateText).join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("remove_versions_title")}
            </h3>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {plan.rows.map((tax) => (
                <li key={tax.id} className="flex items-center gap-2 px-3 py-2">
                  <ColocaBadge variant="default" className="text-muted-foreground">
                    v{tax.version}
                  </ColocaBadge>
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">{rateText(tax)}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {formatDateTime(tax.valid_from, { locale: activeLanguage })}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
        </div>
      )}
    </ColocaAlertDialog>
  );
}
